import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetServerSideProps } from 'next';
import Page from '@noqo/clients/web/Page';
import { NOQOEnabledVariables } from '@noqo/types';
import { useDecision } from '@optimizely/react-sdk';
import initializePage from '../../../app/common/pageSetup';
import CenteredContainer from '../../../app/common/CenteredContainer';
import { NATIONAL } from '../../../app/localization/constants';
import LocalizedMenuPage from '@/menu/categories/localized/LocalizedMenuPage';
import NationalMenuPage from '@/menu/categories/national/NationalMenuPage';
import { resetGetProductsGqlError } from '../../../app/graphql/errors/actions';
import { CategoryDisplayNameProvider } from '@/menu/categories/context/CategoryDisplayNameProvider';
import { isNational } from '../../../app/graphql/gqlDataManipulation';
import { RootState } from '@/rootStateTypes';
import { ErrorPage } from '@/ErrorPage';

interface CategoryPageProps {
  pageID: string;
  storeNumber: string;
}

const hasProductsGqlError = (
  state: RootState
): boolean => state.presentational.gqlErrors.getProductsError;

const CategoryPage = ({ pageID, storeNumber }: CategoryPageProps): JSX.Element => {
  const hasGqlError = useSelector(hasProductsGqlError);
  const isLocalizedOnRedux = useSelector((state: RootState): boolean => state.domain.localization.localizedStore !== NATIONAL);
  const isLocalizedOnServer = !isNational(storeNumber);
  const isLocalized = isLocalizedOnRedux || isLocalizedOnServer;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetGetProductsGqlError());
  }, [dispatch]);

  if (hasGqlError) {
    return <ErrorPage />;
  }

  const PageTemplate = (isLocalized)
    ? LocalizedMenuPage : NationalMenuPage;

  return (
    <>
      <CenteredContainer>
        <CategoryDisplayNameProvider categoryID={pageID}>
          <PageTemplate />
        </CategoryDisplayNameProvider>
      </CenteredContainer>
    </>
  );
};

const Category = ({ pageID, storeNumber }: CategoryPageProps): JSX.Element => {
  const [{ enabled: isNoqoEnabled, variables: noqoEnabledVariables }] = useDecision('fr-web-4576-noqo_enabled_categories');
  const [{ enabled: isNoqoDipsEnabled }] = useDecision('fr-noqo-35-dips-enabled');
  const [{ enabled: isNoqoPartyOfOneEnabled }] = useDecision('fr-noqo-355-party-of-one-enabled-2');

  const isLocalizedOnRedux = useSelector((state: RootState): boolean => state.domain.localization.localizedStore !== NATIONAL);
  const isLocalizedOnServer = !isNational(storeNumber);
  const isLocalized = isLocalizedOnRedux || isLocalizedOnServer;

  if (isLocalized) {
    switch (pageID) {
    case 'dips':
      if (isNoqoDipsEnabled) {
        return <Page slug={pageID} />;
      }
      break;
    case 'partyofone':
      if (isNoqoPartyOfOneEnabled) {
        return <Page slug={pageID} />;
      }
      break;
    default:
      if (
        isNoqoEnabled &&
        (noqoEnabledVariables as NOQOEnabledVariables).enabled.categories.includes(pageID)
      ) {
        return <Page slug={pageID} />; // TODO: NOQO - Replace with actual page slug
      }
      break;
    }
  }

  return <CategoryPage pageID={pageID} storeNumber={storeNumber} />
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const categoryParam = ctx?.query?.category || [];
  const category = typeof categoryParam === 'string' ? categoryParam : categoryParam[0];
  return initializePage(ctx, category, true);
};

export default Category;
